import JSZip from 'jszip'

import { ScheduledPostsClient } from 'api/clients/ScheduledPostsClient'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useRootStore } from 'stores/RootStore'
import { downloadFile } from 'components/VisualStudioPlayground/VisualEditor/utils/export/downloadFile'
import { ChannelType } from 'components/CampaignCreationModal/Types'

export const useExportToZip = (articleId: string) => {
  const filesStore = useRootStore().files
  const file = filesStore.get(articleId)
  const workspaceId = useCurrentWorkspaceId()
  const docStore = useRootStore().docStore

  const exportToZip = async ({ mediaOnly = false }: { mediaOnly?: boolean } = {}) => {
    const scheduledPostId = file?.scheduled_post_id

    if (!scheduledPostId) {
      showToastMessage('Unable to export post content', 'error')
      return
    }

    const abortController = new AbortController()

    const res = await ScheduledPostsClient.show({ workspaceId, scheduledPostId })
    const scheduledPost = res.data.scheduled_post

    const progressMessage = `Downloading ${mediaOnly ? 'post media...' : 'your copy and media...'}`
    const successMessage = `Post ${mediaOnly ? '' : 'copy &'} media saved to your device`

    const dismissToast = showToastMessage(progressMessage, 'loading', {
      variant: 'cancel',
      type: 'button',
      label: 'Cancel',
      onPress: () => {
        abortController.abort()
      },
    })

    const generateZipFile = () => {
      return new Promise(async (resolve, reject) => {
        abortController.signal.addEventListener('abort', () => {
          reject(new Error('Download canceled'))
        })

        const zip = new JSZip()
        if (!mediaOnly) {
          zip.file('content.txt', scheduledPost.content)
        }

        if (docStore && file.channel === ChannelType.BlogPost) {
          let imageNumber = 1
          for (const imageStore of docStore.imageStores.values()) {
            const response = await fetch(imageStore.attrs.src)
            const contentType = response.headers.get('content-type')

            if (response.status === 200 && contentType) {
              const [fileType, ext] = contentType.split('/')
              const filename = `${imageNumber <= 9 ? `0${imageNumber}` : imageNumber}_${fileType}.${ext}`
              const blob = await response.blob()
              zip.file(filename, blob)
              imageNumber++
            }
          }
        } else {
          for (let i = 0; i < scheduledPost.media_objects.length; i++) {
            const mediaObject = scheduledPost.media_objects[i]
            const res = await fetch(mediaObject.source_url)
            const imageNumber = i + 1

            if (res.status === 200) {
              const [fileType, ext] = mediaObject.mime_type.split('/')
              const filename = `${imageNumber <= 9 ? `0${imageNumber}` : imageNumber}_${fileType}.${ext}`
              const blob = await res.blob()
              zip.file(filename, blob)
            }
          }
        }

        const content = await zip.generateAsync({ type: 'blob' })
        resolve(content)
      })
    }

    try {
      const content = await generateZipFile()
      downloadFile(new File([content as Blob], `${scheduledPost.source_title}.zip`))
      dismissToast()
      showToastMessage(successMessage)
      return true
    } catch (e) {
      dismissToast()
      return false
    }
  }

  return { exportToZip }
}
